import React, { useState } from 'react';
import { message, Button, Modal, Select, Tag, Typography } from 'antd';
import styled from 'styled-components/macro';

import { EntityType, SearchResult, TermRelationshipType } from '../../../../types.generated';
import { useAddRelatedTermsMutation } from '../../../../graphql/glossaryTerm.generated';
import { useAddTypeOfRelationsMutation } from '../../../../graphql/mutations.generated';
import { useGetSearchResultsLazyQuery } from '../../../../graphql/search.generated';

import GlossaryBrowser from '../../../glossary/GlossaryBrowser/GlossaryBrowser';
import { BrowserWrapper } from '../../../shared/tags/AddTagsTermsModal';
import ClickOutside from '../../../shared/ClickOutside';
import TermLabel from '../../../shared/TermLabel';

import { RelatedTermTypes } from '../../shared/components/styled/search/types';
import { useEntityData, useRefetch } from '../../shared/EntityContext';
import { useEntityRegistry } from '../../../useEntityRegistry';
import { useTranslation } from '../../../../i18n/useTranslation';

const StyledSelect = styled(Select)`
    width: 480px;
`;

interface Props {
    onClose: () => void;
    entityType?: EntityType;
    isTypeTermGroup?: boolean;
    relationshipType?: TermRelationshipType;
    isAddingTermFromNode?: boolean;
}

const ASSOCIATED_WITH = 'associatedWithGlossary';
const UTILIZED_BY = 'utilizedBy';

type RelationshipType = TermRelationshipType | RelatedTermTypes | undefined;

function AddRelatedTermsModal(props: Props) {
    const { onClose, relationshipType, entityType, isTypeTermGroup, isAddingTermFromNode } = props;

    const [inputValue, setInputValue] = useState('');
    const [selectedUrns, setSelectedUrns] = useState<any[]>([]);
    const [selectedTerms, setSelectedTerms] = useState<any[]>([]);
    const [isFocusedOnInput, setIsFocusedOnInput] = useState(false);

    const getInitialRelationship = (): RelationshipType => {
        if (isAddingTermFromNode) return RelatedTermTypes.isTypeOf;
        if (isTypeTermGroup) return RelatedTermTypes.typeOfRelations;
        return relationshipType;
    };

    const [relationship, setRelationship] = useState<RelationshipType>(getInitialRelationship());

    const entityRegistry = useEntityRegistry();
    const { urn: entityDataUrn } = useEntityData();
    const { t } = useTranslation();
    const refetch = useRefetch();

    const [AddRelatedTerms] = useAddRelatedTermsMutation();
    const [AddTypeOfRelations] = useAddTypeOfRelationsMutation();

    const isTypeOfByRelationship = relationship === RelatedTermTypes.typeOfRelations;
    const isAddingTermFromNodeAndIsTypeOf =
        isAddingTermFromNode && (isTypeOfByRelationship || relationship === RelatedTermTypes.isTypeOf);
    /**
     * If the user is adding term from node and the relationship is Type Of, then we use the AddTypeOfRelations mutation
     * If the user is adding term from term group or adding term group to term group, then we use the AddTypeOfRelations mutation
     * If the user is adding term from term or adding term group from term, then we use the AddRelatedTerms mutation
     */
    const shouldUseAddTypeOfRelations = isTypeOfByRelationship || isAddingTermFromNodeAndIsTypeOf;

    function addTerms() {
        // Here, we check if the user adds the term on the same term, then the user cannot perform that action
        if (selectedUrns.includes(entityDataUrn)) {
            message.error({ content: t('Cannot create relation with the same term.'), duration: 3 });
            return;
        }

        const urn = isAddingTermFromNode ? selectedUrns[0] : entityDataUrn;
        const nodeUrns = isAddingTermFromNode ? [entityDataUrn] : selectedUrns;

        const variables: any = shouldUseAddTypeOfRelations
            ? { input: { urn, nodeUrns } }
            : { input: { urn: entityDataUrn, termUrns: selectedUrns, relationshipType: relationship } };

        const addRelation = shouldUseAddTypeOfRelations ? AddTypeOfRelations : AddRelatedTerms;

        addRelation({ variables })
            .catch((e) => {
                message.destroy();
                message.error({ content: `${t('Failed to move: \n')} ${e.message || ''}`, duration: 3 });
            })
            .finally(() => {
                message.loading({ content: t('Adding...'), duration: 2 });
                setTimeout(() => {
                    message.success({
                        content: t('Added Successfully'),
                        duration: 2,
                    });
                    refetch();
                }, 2000);
            });
        onClose();
    }

    const [termSearch, { data: termSearchData }] = useGetSearchResultsLazyQuery();
    const termSearchResults = termSearchData?.search?.searchResults || [];

    const tagSearchOptions = termSearchResults.map((result: SearchResult) => {
        const displayName = entityRegistry.getDisplayName(result.entity.type, result.entity);

        return (
            <Select.Option value={result.entity.urn} key={result.entity.urn} name={displayName}>
                <TermLabel name={displayName} />
            </Select.Option>
        );
    });

    const handleSearch = (text: string) => {
        if (text.length > 0) {
            // Use GlossaryNode when adding term groups, GlossaryTerm otherwise
            const searchEntityType = isTypeTermGroup ? EntityType.GlossaryNode : EntityType.GlossaryTerm;

            termSearch({
                variables: {
                    input: {
                        type: searchEntityType,
                        query: text,
                        start: 0,
                        count: 20,
                    },
                },
            });
        }
    };

    // When a Tag or term search result is selected, add the urn to the Urns
    const onSelectValue = (urn: string) => {
        const newUrns = [...selectedUrns, urn];
        setSelectedUrns(newUrns);
        const selectedSearchOption = tagSearchOptions.find((option) => option.props.value === urn);
        setSelectedTerms([...selectedTerms, { urn, component: <TermLabel name={selectedSearchOption?.props.name} /> }]);
    };

    // When a Tag or term search result is deselected, remove the urn from the Owners
    const onDeselectValue = (urn: string) => {
        const newUrns = selectedUrns.filter((u) => u !== urn);
        setSelectedUrns(newUrns);
        setInputValue('');
        setIsFocusedOnInput(true);
        setSelectedTerms(selectedTerms.filter((term) => term.urn !== urn));
    };

    function selectTermFromBrowser(urn: string, displayName: string) {
        setIsFocusedOnInput(false);
        const newUrns = [...selectedUrns, urn];
        setSelectedUrns(newUrns);
        setSelectedTerms([...selectedTerms, { urn, component: <TermLabel name={displayName} /> }]);
    }

    function clearInput() {
        setInputValue('');
        setTimeout(() => setIsFocusedOnInput(true), 0); // call after click outside
    }

    function handleBlur() {
        setInputValue('');
    }

    const tagRender = (properties) => {
        // eslint-disable-next-line react/prop-types
        const { closable, onClose: close, value } = properties;
        const onPreventMouseDown = (event) => {
            event.preventDefault();
            event.stopPropagation();
        };
        const selectedItem = selectedTerms.find((term) => term.urn === value).component;

        return (
            <Tag
                onMouseDown={onPreventMouseDown}
                closable={closable}
                onClose={close}
                style={{
                    marginRight: 3,
                    display: 'flex',
                    justifyContent: 'start',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                    opacity: 1,
                    color: '#434343',
                    lineHeight: '16px',
                }}
            >
                {selectedItem}
            </Tag>
        );
    };

    const getTitleName = () => {
        if (entityType === EntityType.GlossaryTerm) {
            return isTypeTermGroup ? t('Term Group') : t('Terms');
        }

        return t('Term Groups');
    };

    const getSelectEntity = () => {
        // If specifically adding term groups, only allow selecting nodes
        if (isTypeTermGroup) {
            return { selectNode: selectTermFromBrowser };
        }

        // If adding to a glossary term and relationship is Type Of, allow selecting nodes
        if (entityType === EntityType.GlossaryTerm && isTypeOfByRelationship) {
            return { selectNode: selectTermFromBrowser, selectTerm: selectTermFromBrowser };
        }

        // Default for glossary terms: only allow selecting terms
        if (entityType === EntityType.GlossaryTerm) {
            return { selectTerm: selectTermFromBrowser };
        }

        // For other entity types, allow nodes if Type Of relationship
        return isTypeOfByRelationship ? { selectNode: selectTermFromBrowser, hideTerms: true } : {};
    };
    const isShowingGlossaryBrowser = !inputValue && isFocusedOnInput;

    const selectEntity = getSelectEntity();

    const titleName = getTitleName();

    const getRelationshipOptions = () => {
        if (isAddingTermFromNode) {
            return [
                {
                    label: RelatedTermTypes.isTypeOf,
                    value: RelatedTermTypes.isTypeOf,
                },
                {
                    label: RelatedTermTypes.hasRelatedTerms,
                    value: TermRelationshipType.HasA,
                },
                {
                    label: RelatedTermTypes.isRelatedTerms,
                    value: TermRelationshipType.IsA,
                },
            ];
        }

        const baseOptions = !isTypeTermGroup
            ? [
                  {
                      label: RelatedTermTypes.hasRelatedTerms,
                      value: TermRelationshipType.HasA,
                  },
                  {
                      label: RelatedTermTypes.isRelatedTerms,
                      value: TermRelationshipType.IsA,
                  },
                  {
                      label: RelatedTermTypes.utilizedByTerms,
                      value: UTILIZED_BY,
                  },
                  {
                      label: RelatedTermTypes.associatedWithTerms,
                      value: ASSOCIATED_WITH,
                  },
              ]
            : [];

        return [
            ...baseOptions,
            {
                label: RelatedTermTypes.typeOfRelations,
                value: RelatedTermTypes.typeOfRelations,
            },
        ];
    };

    return (
        <Modal
            className="f-modal"
            title={`${t('Add Related')} ${titleName}`}
            visible
            centered
            onCancel={onClose}
            footer={
                <>
                    <Button onClick={addTerms} disabled={!selectedUrns.length || !relationship} type="primary">
                        {t('Add')}
                    </Button>
                    <Button onClick={onClose} type="default">
                        {t('Cancel')}
                    </Button>
                </>
            }
        >
            <ClickOutside onClickOutside={() => setIsFocusedOnInput(false)}>
                <StyledSelect
                    autoFocus
                    showSearch
                    mode="multiple"
                    onBlur={handleBlur}
                    filterOption={false}
                    onClear={clearInput}
                    value={selectedUrns}
                    tagRender={tagRender}
                    onSearch={(value: string) => {
                        // eslint-disable-next-line react/prop-types
                        handleSearch(value.trim());
                        // eslint-disable-next-line react/prop-types
                        setInputValue(value.trim());
                    }}
                    defaultActiveFirstOption={false}
                    onFocus={() => setIsFocusedOnInput(true)}
                    placeholder={`${t('Search for Glossary')} ${titleName}...`}
                    onSelect={(asset: any) => onSelectValue(asset)}
                    onDeselect={(asset: any) => onDeselectValue(asset)}
                    dropdownStyle={isShowingGlossaryBrowser || !inputValue ? { display: 'none' } : {}}
                >
                    {tagSearchOptions}
                </StyledSelect>
                <BrowserWrapper isHidden={!isShowingGlossaryBrowser}>
                    <GlossaryBrowser isSelecting {...selectEntity} hideTerms={isTypeTermGroup} />
                </BrowserWrapper>
                <div className="mt-4">
                    <Typography.Text className="f-text-gray-s100">{t('Select Term Relationship')}</Typography.Text>
                    <Select
                        value={relationship}
                        placeholder={t('Select Relationship Type')}
                        style={{ width: '100%', marginTop: 4 }}
                        onChange={setRelationship}
                        options={getRelationshipOptions()}
                    />
                </div>
            </ClickOutside>
        </Modal>
    );
}

export default AddRelatedTermsModal;
